import { authFetch } from '../../utils/authFetch';
import {
    deletePending,
    getPending,
    listPendingIds,
    savePending,
    saveCache
} from './blobStore';

/**
 * Drains the local `pending` blob store to the server, one media id at a time.
 * The synced metadata doc is pushed by RxDB independently; the binary rides this
 * queue so capture works fully offline and uploads catch up when connectivity
 * (and a valid token) return.
 *
 * Retry model: the queue is the source of truth. A failed/again-needed upload
 * simply stays pending and is retried on the next tick (capture, `online`
 * event, or the interval). A 404 means the metadata push hasn't landed yet (or
 * the doc was deleted) — we leave it for a later tick rather than dropping it.
 */

const RETRY_INTERVAL_MS = 15000;

let running = false;
let intervalId: ReturnType<typeof setInterval> | null = null;
let onlineHandler: (() => void) | null = null;

/** Queue a freshly-captured image: keep a display copy + a pending upload. */
export async function enqueueUpload(
    id: string,
    blob: Blob,
    mimeType: string
): Promise<void> {
    await Promise.all([
        savePending(id, blob, mimeType),
        // Cache it too so the capturing device renders instantly and offline,
        // independent of whether the upload has completed.
        saveCache(id, blob)
    ]);
    void kickUpload();
}

/** Trigger a drain pass (no-op if one is already running or we're offline). */
export async function kickUpload(): Promise<void> {
    if (running) {
        return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return;
    }
    running = true;
    try {
        const ids = await listPendingIds();
        for (const id of ids) {
            const entry = await getPending(id);
            if (!entry) {
                continue;
            }
            try {
                const response = await authFetch(`/api/media/${id}/blob`, {
                    method: 'POST',
                    headers: {
                        'Content-Type':
                            entry.mimeType || 'application/octet-stream'
                    },
                    body: entry.blob
                });
                if (response.ok) {
                    await deletePending(id);
                    continue;
                }
                if (response.status === 401) {
                    // Not authenticated yet — stop; a later tick retries.
                    break;
                }
                // 404 (metadata not synced yet) or 5xx — leave pending, retry later.
            } catch {
                // Network error — stop the pass; retry on the next tick.
                break;
            }
        }
    } finally {
        running = false;
    }
}

/** Start the background uploader (idempotent). Resumes any interrupted uploads. */
export function startMediaUploader(): void {
    if (intervalId !== null) {
        return;
    }
    onlineHandler = () => {
        void kickUpload();
    };
    window.addEventListener('online', onlineHandler);
    intervalId = setInterval(() => {
        void kickUpload();
    }, RETRY_INTERVAL_MS);
    void kickUpload();
}

export function stopMediaUploader(): void {
    if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
    }
    if (onlineHandler) {
        window.removeEventListener('online', onlineHandler);
        onlineHandler = null;
    }
}
